import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { 
  getFirestore,
  initializeFirestore,
  CACHE_SIZE_UNLIMITED,
  terminate,
  waitForPendingWrites,
  persistentLocalCache,
  persistentMultipleTabManager,
  enableIndexedDbPersistence
} from 'firebase/firestore';

const FirebaseContext = createContext(null);

// Create a singleton instance
let firebaseInstance = null;

async function initializeFirebaseApp() {
  if (firebaseInstance) {
    return firebaseInstance;
  }

  const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
  };

  try {
    // Initialize or get existing Firebase app
    const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
    const auth = getAuth(app);

    // Initialize Firestore with modern cache settings
    const db = initializeFirestore(app, {
      cacheSizeBytes: CACHE_SIZE_UNLIMITED,
      experimentalForceLongPolling: true,
      cache: persistentLocalCache({
        tabManager: persistentMultipleTabManager()
      })
    });

    firebaseInstance = { app, auth, db, isTerminated: false };
    return firebaseInstance;
  } catch (error) {
    console.error('Firebase initialization error:', error);
    throw error;
  }
}

export function FirebaseProvider({ children }) {
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const instanceRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    async function initialize() {
      try {
        const instance = await initializeFirebaseApp();
        if (mounted) {
          instanceRef.current = instance;
          setInitialized(true);
          setError(null);
          console.log('Firebase initialized successfully with persistent cache');
        }
      } catch (err) {
        console.error('Firebase initialization error:', err);
        if (mounted) {
          setError(err.message);
          setInitialized(false);
          instanceRef.current = null;
        }
      }
    }

    initialize();

    // Cleanup function
    return () => {
      mounted = false;
      // Don't terminate here - we want to keep the instance alive
    };
  }, []);

  // Handle window unload to clean up properly
  useEffect(() => {
    const handleUnload = async () => {
      if (instanceRef.current?.db && !instanceRef.current.isTerminated) {
        try {
          await waitForPendingWrites(instanceRef.current.db);
          await terminate(instanceRef.current.db);
          instanceRef.current.isTerminated = true;
        } catch (e) {
          console.warn('Cleanup error:', e);
        }
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  const value = React.useMemo(() => ({
    ...instanceRef.current,
    isInitialized: initialized,
    error
  }), [initialized, error]);

  if (error) {
    return (
      <div className="text-red-500 p-4 border border-red-300 rounded bg-red-50">
        <h3 className="font-bold">Firebase Error</h3>
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-2 px-4 py-2 bg-red-100 hover:bg-red-200 rounded transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!initialized || !instanceRef.current) {
    return (
      <div className="text-gray-500 p-4 flex items-center justify-center">
        <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        Initializing Firebase...
      </div>
    );
  }

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
}

export function useFirebase() {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
}
